import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import type { PetEntry } from "../../types";
import { PET_TYPE_OPTIONS } from "../../data";
import { FIELD_BORDER, FIELD_RADIUS, inputSx } from "../../styles";

type PetsDialogProps = {
  open: boolean;
  onClose: () => void;
  petDrafts: PetEntry[];
  onPetDraftsChange: (next: PetEntry[]) => void;
  onSave: () => void;
};

const PetsDialog = ({
  open,
  onClose,
  petDrafts,
  onPetDraftsChange,
  onSave,
}: PetsDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    scroll="body"
    PaperProps={{
      sx: {
        borderRadius: "20px",
        px: { xs: 2.5, sm: 3 },
        py: { xs: 2, sm: 2.5 },
        overflow: "visible",
      },
    }}
  >
    <DialogTitle
      sx={{
        p: 0,
        mb: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Pet Details
      </Typography>
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 0, overflow: "visible" }}>
      <Stack spacing={2.5}>
        <Typography sx={{ color: "#4B5563", fontSize: "14px" }}>
          Tell us what you've owned - this helps match you with the right designer.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "7fr 4fr 1fr",
            columnGap: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
            What Kind of Pets?
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
            How Many?
          </Typography>
          <Box />
        </Box>
        <Stack spacing={2}>
          {petDrafts.map((pet, index) => {
            const parsedCount = Number.parseInt(pet.count, 10);
            const countValue = Number.isNaN(parsedCount) ? 1 : parsedCount;
            return (
              <Box
                key={`pet-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "7fr 4fr 1fr",
                  columnGap: 2,
                  alignItems: "center",
                }}
              >
                <TextField
                  label="Pet Type"
                  value={pet.type}
                  onChange={(event) => {
                    const value = event.target.value;
                    onPetDraftsChange(
                      petDrafts.map((entry, entryIndex) =>
                        entryIndex === index ? { ...entry, type: value } : entry
                      )
                    );
                  }}
                  size="small"
                  fullWidth
                  select
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                >
                  {PET_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <Box
                  sx={{
                    border: `1px solid ${FIELD_BORDER}`,
                    borderRadius: FIELD_RADIUS,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      const next = Math.max(1, countValue - 1);
                      onPetDraftsChange(
                        petDrafts.map((entry, entryIndex) =>
                          entryIndex === index ? { ...entry, count: String(next) } : entry
                        )
                      );
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600 }}>{countValue}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const next = countValue + 1;
                      onPetDraftsChange(
                        petDrafts.map((entry, entryIndex) =>
                          entryIndex === index ? { ...entry, count: String(next) } : entry
                        )
                      );
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {petDrafts.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        onPetDraftsChange(
                          petDrafts.filter((_, entryIndex) => entryIndex !== index)
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
        <Button
          variant="outlined"
          onClick={() => onPetDraftsChange([...petDrafts, { type: "", count: "1" }])}
          sx={{
            textTransform: "none",
            borderStyle: "dashed",
            borderColor: "#E25A8A",
            color: "#C2185B",
            borderRadius: FIELD_RADIUS,
            py: 1.2,
            fontWeight: 600,
            "&:hover": {
              borderColor: "#C2185B",
              backgroundColor: "rgba(194, 24, 91, 0.04)",
            },
          }}
        >
          + Add another pet type
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          fullWidth
          sx={{
            textTransform: "none",
            backgroundColor: "#1C1C1C",
            borderRadius: "12px",
            py: 1.2,
            fontWeight: 600,
            "&:hover": { backgroundColor: "#1C1C1C" },
          }}
        >
          Save Pets
        </Button>
      </Stack>
    </DialogContent>
  </Dialog>
);

export default PetsDialog;
